import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { ModuleManager } from "src/shared/models/module-manager";
import { AgGridHelperService } from "src/app/reusables/services/ag-grid-helper.service";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { GridApi, GridOptions } from "ag-grid-community";
import { CvConfigurationService } from "src/app/reusables/services/cv-configuration.service";
import { MetricReviewService } from "./metric-review.service";
import { MetricReviewData } from "./metric-review.models";
import { AgGridAngular } from "ag-grid-angular";

@Component({
    selector: "cv-metric-review",
    templateUrl: "./metric-review.component.html",
    styleUrls: ["./metric-review.component.scss"],
})
export class MetricReviewComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;
    @ViewChild('metricReviewWidgetGrid') agGrid: AgGridAngular;

    data: MetricReviewData = null;

    private loadingPromise: Promise<any> = null; // The promise from getData is stored so pending requests can be canceled if settings change.
    private abortLoad: () => void = null; // Function to abort the current load
    private isAborting = false; // Flag to track if we're in the process of aborting
    isFirstLoad = true; // Used for logging in endpoint
    isRequestingLocalizations = false;
    loadingStates = { config: true, data: true, localizations: true }; // Tracks loading for spinner.  Update through setLoadingState()
    localizationStrings = null;
    sortModel = null; // The sorted column state of the ag-grid.
    stateSaveTimer = null; // Used to debounce the save states
    restoreInProgress = false;
    filterValue = "";
    searchOpen = false;
    searchPlaceholder = "";

    didLastFetchFail = false;
    gridOptions: GridOptions = null;
    gridApi: GridApi;
    private gridState: any = null; // This object holds previous state for ag-grid, and saves it to
    tableState: any = null;
    isFetching = false;
    isFirstDataLoaded = false;
    isGridReady = false;
    isNoMetric = false;
    noData = false;

    private isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');
    localeText = {};
    metricText: string;
    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: MetricReviewService,
        private configService: CvConfigurationService
    ) {}
    
     private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; };

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        await this.loadLocalizationsAsync();

        this.moduleManager.setOptions({
            onSettingsApplied: () => {
                this.setLoadingState("config", false);
                this.setLoadingState("data", true);
                this.abortGetData();
                this.getData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: (isAutoRefresh) => {
                if (!isAutoRefresh) {
                    this.setLoadingState("data", true);
                }
                return this.getData(isAutoRefresh);
            },
            canRefresh: () => !this.isFetching && !this.isLoading(),
            onExport: (format, isCXOne) => this.service.loadDataForExport(this.moduleKey, format, isCXOne),
        });

        this.moduleManager.addHeaderItem({
            icon: "search",
            click: () => this.toggleOpenSearch(),
            name: "searchIcon"
        });

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                if (!stateData) { // No table state loaded
                    return;
                }
                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true,
        });

        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => {
                return this.gridState;
            },
            onStateLoaded: stateData => {
                if (!stateData) { // No table state loaded
                    return;
                }
                this.gridState = stateData;
                if (this.gridApi && this.gridState)
                    this.agGridHelperService.loadGridState(this.gridApi, stateData);
            },
            autoLoad: true
        });

        this.configureAgGrid();
    }

    setLoadingState(subState, value) {
        this.loadingStates[subState] = value;
    }

    // The primary scenario of abort: When this module's settings are changed.
    abortGetData() {
        if (this.loadingPromise) {
            this.isAborting = true;
            this.abortLoad?.();
            this.loadingPromise = null;
            this.abortLoad = null;
        }
    }

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.gridApi || this.restoreInProgress)
            return;
        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.gridApi);
        if (tableState != null) {
            this.tableState = tableState;
        }
        // tell module manager to save the state
        this.queueStateSave();
    };

    /**
     * This function debounces state saves to 1s.
     */
    queueStateSave() {
        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }
        this.stateSaveTimer = window.setTimeout(() => {
            this.moduleManager.saveState("sortedColumn");
            this.stateSaveTimer = null;
            if (this.gridApi && !this.isFirstLoad)
                this.gridState = this.agGridHelperService.getGridState(this.gridApi);
            this.moduleManager.saveState(); // This will save all moduleManager.addState definition
        }, 1000);
    }

    /**
     * This function is where the ag-grid gridOptions is configured.
     */
    public async configureAgGrid() {
        const localizedString = this.cvLocalizationService.getCachedText("Common", "Original_Metric", "Original Metric Name");
        // This function assumes the localizationStrings have been set.
        // Since the backend is returning the real Metric object, the abbreviation (MetricName if empty) is returned for the value of that cell.
        const metricNameValueGetter = (params) => {
            const metric = params.data.Metric;
            const abbr = metric.Abbreviation || "";
            if (metric.MetricCustomName && metric.MetricCustomName !== "")
                return metric.MetricCustomName + "*";
            if (abbr.trim().length > 1) return abbr;
            else {
                return metric.MetricName;
            }
        };

        // Since the value represented in the Metric column can be the abbreviation, this tooltip getter returns the actual MetricName
        function metricNameTooltipGetter (params) {
            return params.data.Metric.MetricCustomName && params.data.Metric.MetricCustomName != "" 
                ? localizedString + ": " + '\n' + params.data.Metric.Abbreviation
                : params.data.Metric.Abbreviation;
        }

        this.gridOptions = this.agGridHelperService.createGridOptions({
            animateRows: true,
            context: {
                getMetric: params => params.data.Metric,
            },
            getMainMenuItems: params => this.agGridHelperService.hideResetColumnsMenuItem(params, this.moduleManager.hasWriteAccess()),
            defaultColDef: {
                editable: false,
                minWidth: this.agGridHelperService.defaultMinWidth,
                lockPinned: true,
                lockVisible: true,
                resizable: true,
                sortable: true,
                tooltipValueGetter: (params) => {
                    if (params.valueFormatted) {
                        return params.valueFormatted;
                    } else {
                        return params.value.MetricDisplayValue ?? "";
                    }
                },
                icons: this.configService.isCxOne
                    ? {
                        sortAscending: `
                            <div class="sortIcons">
                                <svg class="icon-10 fill-cxoneBlue">
                                    <use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use>
                                </svg>
                                <svg class="icon-10 fill-sort">
                                    <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                                </svg>
                            </div>
                        `,
                        sortDescending: `
                            <div class="sortIcons">
                                <svg class="icon-10 fill-sort">
                                    <use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use>
                                </svg>
                                <svg class="icon-10 fill-cxoneBlue">
                                    <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                                </svg>
                            </div>
                        `,
                        filter: `
                            <svg class="icon-20 fill-iconMenu">
                                <use href="../App_Themes/CXone/icons/filter.svg#filter"></use>
                            </svg>
                        `,
                        menu: `
                            <svg class="icon-20 fill-iconMenu">
                                <use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use>
                            </svg>
                        `,
                        columns: `
                            <svg class="icon-20 fill-iconMenu">
                                <use href="../App_Themes/CXone/icons/chart.svg#chart"></use>
                            </svg>
                        `,
                    }
                    : null,
                    menuTabs: this.agGridHelperService.getMenuTabItems(this.moduleManager.hasWriteAccess()),
                },
                colResizeDefault: "shift",
                columnDefs: [
                    {

                        colId: "metric",
                        comparator: (valueA, valueB) => valueA.localeCompare(valueB, undefined, { sensitivity: "accent" }),
                        headerName: this.metricText,
                        tooltipValueGetter: metricNameTooltipGetter,
                        valueGetter: metricNameValueGetter,
                        filter: 'agTextColumnFilter',
                        suppressMenu: this.isSharedURL,
                    },
                    {
                        colId: "Today",
                        field: "StartPeriodDate",
                        headerName: this.moduleManager.customSettings.CustomDatesRanges?.getValue().firstDate.displayName,
                        type: "metricColumn",
                        sortable: true,
                        filter: 'agTextColumnFilter',
                        suppressMenu: this.isSharedURL,
                    },
                    {
                        colId: "Week",
                        field: "ComparisonDate1",
                        headerName: this.moduleManager.customSettings.CustomDatesRanges?.getValue().secondDate.displayName,
                        type: "metricColumn",
                        sortable: true,
                        filter: 'agTextColumnFilter',
                        suppressMenu: this.isSharedURL,
                    },
                    {
                        colId: "Month",
                        field: "ComparisonDate2",
                        headerName: this.moduleManager.customSettings.CustomDatesRanges?.getValue().thirdDate.displayName,
                        type: "metricColumn",
                        sortable: true,
                        filter: 'agTextColumnFilter',
                        suppressMenu: this.isSharedURL,
                    },
                ],
                enableBrowserTooltips: true,
                localeText: await this.agGridHelperService.getLocalizations(),
                onGridReady: (agEvent) => {
                    this.gridApi = agEvent.api;
                    if (this.data)
                    	this.gridApi.setGridOption('rowData', this.data.Metrics);
                    this.restoreColumnState();
                    if (!this.gridState) {
                        this.agGridHelperService.sizeColumnsSmart(agEvent);
                        this.tableStateChanged();
                    }    
                },
                onColumnVisible: event => {
                    if (this.tableState == null || this.tableState.columns.map(c => c.width).filter(w => w != null).length === 0)
                        this.agGridHelperService.sizeColumnsSmart(event);
                    this.updateTableState();
                },
                onSortChanged: event => {
                    this.queueStateSave()
                    if (event.source === "uiColumnSorted")
                        this.moduleManager.requestPublishNeeded();
                },
                onColumnMoved: event => {
                    this.queueStateSave();
                    if (event.source === "uiColumnMoved")
                        this.moduleManager.requestPublishNeeded();
                },
                onColumnResized: () => this.updateTableState(),
                onGridSizeChanged: event => {
                    if (this.isResizeWaiting)
                        this.agGridHelperService.sizeColumnsSmart(event);
                },
                getRowId: (data) => data.data.Metric.MetricID,
                overlayNoRowsTemplate: this.configService.isCxOne ?
                    `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
                rowData: [],
                rowSelection: "multiple",
                suppressCellFocus: true,
                suppressContextMenu: true,
                suppressScrollOnNewData: true,
                suppressDragLeaveHidesColumns: true,
            },
            {
                enableCopy: true,
            }
        );

        if (!this.moduleManager.hasWriteAccess() && !this.configService.isCxOne) {
            this.agGridHelperService.setGridReadOnly(this.gridOptions);
        }

        this.isGridReady = true;
    }

    /**
     * If subState isn't specified, this function returns the cumulative result of all the loadingStates.  If any are loading, it returns true.
     * If subState is specified, only the loading state of that subState is returned.
     */
    isLoading(subState?: string) {
        if (!subState) {
            const loading = Object.keys(this.loadingStates).reduce(
                (accumulator, subStateTemp) => accumulator || this.loadingStates[subStateTemp], false);
            return loading;
        } else {
            if (this.loadingStates[subState] !== undefined) {
                return this.loadingStates[subState];
            } else {
                return false;
            }
        }
    }

    private async getData(isAutoRefresh: boolean) {
        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }

        if (this.isFetching) {
            return;
        }

        this.isFetching = true;

        // Create abort promise for cancellation support
        const abort = new Promise<void>((resolve) => this.abortLoad = () => resolve());

        try {
            this.loadingPromise = this.service.getCustomMetricReviewData(
                this.moduleKey,
                isAutoRefresh,
                this.isFirstLoad,
                abort
            );
            const data = await this.loadingPromise;

            this.isFirstLoad = false;
            delete data["BackendMessages"];
            this.data = data;

            if (!this.data || this.data.Metrics?.length === 0) { //Metrics holds data rows
                this.noData = true;
            } else if (this.data.Error === 1) {
                this.didLastFetchFail = false;
                this.isFirstDataLoaded = true;
                this.isNoMetric = true;
                this.setLoadingState("data", false);
                return;
            } else {
                this.noData = false;
            }

            this.isNoMetric = false;

            const headersUpdated = this.getColumnDefsHeadersUpdated(this.gridOptions.columnDefs);

            if (this.isGridReady && this.gridApi) {
                this.gridApi.setGridOption('columnDefs', headersUpdated);
                this.gridApi.setGridOption('rowData', this.data.Metrics);
            }

            this.setLoadingState("data", false);
            this.didLastFetchFail = false;
            this.isFirstDataLoaded = true;
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            if (this.isAborting) {
                this.isAborting = false;
                return;
            }
            console.error("Error fetching metric data.");
            this.didLastFetchFail = true;
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            if (this.gridState && this.gridApi) {
                this.agGridHelperService.loadGridState(this.gridApi, this.gridState);
                this.gridApi.sizeColumnsToFit();
            }
            this.loadingPromise = null;
            this.abortLoad = null;
            this.isFetching = false;
            this.moduleManager.showLoader = false;
        }
    }

    private getColumnDefsHeadersUpdated(columnDefinitions) {
        const customDates = this.moduleManager.customSettings.CustomDateRanges?.getValue();
        columnDefinitions[1].headerName = this.getDateLocaleText(customDates?.firstDate?.name ?? "Today");
        columnDefinitions[2].headerName = this.getDateLocaleText(customDates?.secondDate?.name ?? "Last 7 Days");
        columnDefinitions[3].headerName = this.getDateLocaleText(customDates?.thirdDate?.name ?? "Last 30 Days");
        return columnDefinitions;
    }

    async loadLocalizationsAsync() {
        try {
            const localizationPromises = [
                this.cvLocalizationService.GetSection("Metric_Review_Module"),
                this.cvLocalizationService.GetSection("Common"),
                this.cvLocalizationService.GetSection("Controls"),
            ];
            await Promise.all(localizationPromises);
            this.localeText = await this.agGridHelperService.getLocalizations();
        }
        finally {
            this.setLoadingState("localizations", false);
            this.isRequestingLocalizations = false;
            this.metricText = this.cvLocalizationService.getCachedText("Common", "Metric", "Metric");
            this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
        }
    }

    // applies all the options saved in tableState to the grid
    restoreColumnState() {
        if (this.gridApi && this.gridState)
            this.agGridHelperService.loadGridState(this.gridApi, this.gridState);
    }

    tableStateChanged() {
        this.gridState = this.agGridHelperService.getGridState(this.gridApi);
        this.queueStateSave();
    }

    getDateLocaleText(defaultValue?: string) {
        // replace spaces in default value with underscores
        const key = defaultValue.replace(/\s(?=15)/g, "").replace(/\s+/g, '_');
        return this.cvLocalizationService.getCachedText("Controls", key, defaultValue);
    }

    clearFilter() {
        this.filterValue = "";

        if (this.gridApi) { // notify filter change
            this.gridApi.setGridOption('quickFilterText', this.filterValue);
            this.applyFilter();
        }
    };

    applyFilter() {
        if (this.gridApi) { // notify filter change
            this.gridApi.setGridOption('quickFilterText', this.filterValue.trim());
            if (this.searchOpen && this.gridApi.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.gridApi.showNoRowsOverlay();
            } else {
                this.gridApi.hideOverlay();
            }
        }
    };

    toggleOpenSearch() {
        if (this.searchOpen) {
            this.searchOpen = false;
            this.gridApi?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.searchOpen = true;
            this.gridApi?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    };

    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.configService.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            } else {
                this.searchBox?.nativeElement?.focus();
            }
        }, 200);
    }
}
